import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";
import { BookOpen, Award, CheckCircle2, ArrowRight, Download, Trophy, Sparkles, Briefcase, Receipt, Ticket, CreditCard, Eye, X } from "lucide-react";
import PayPalUnlockButton from "@/components/PayPalUnlockButton";
import LegalDisclaimer from "@/components/LegalDisclaimer";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  const [modules, setModules] = useState([]);
  const [practice, setPractice] = useState(null);
  const [purchases, setPurchases] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [p, m, pr, ph] = await Promise.all([
          api.get("/progress"),
          api.get("/modules"),
          api.get("/practice/scores"),
          api.get("/payment/history"),
        ]);
        setProgress(p.data);
        setModules(m.data.modules);
        setPractice(pr.data);
        setPurchases(ph.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const percent = progress?.percent || 0;
  const cert = progress?.certificate;
  const canTakeExam = percent === 100;

  const downloadCert = () => {
    if (!cert) return;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/certificate/${cert.certificate_id}/pdf`;
    // Auth via httpOnly cookie (credentials: include)
    fetch(url, { credentials: "include" })
      .then((r) => r.blob())
      .then((blob) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `bbp-certificate-${cert.certificate_id}.pdf`;
        link.click();
      })
      .catch((err) => console.error("Certificate download failed", err));
  };

  const openPreview = async () => {
    if (!cert) return;
    setPreviewOpen(true);
    setPreviewLoading(true);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/certificate/${cert.certificate_id}/pdf`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error(`preview_${res.status}`);
      const blob = await res.blob();
      setPreviewUrl(URL.createObjectURL(blob));
    } catch (err) {
      console.error("Certificate preview failed", err);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewOpen(false);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar />

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-10 fade-in">
          <div className="text-xs font-mono uppercase tracking-widest text-red-600 mb-2">Dashboard</div>
          <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight">
            Welcome, {user?.name?.split(" ")[0] || "Trainee"}.
          </h1>
          <p className="text-zinc-600 mt-2">Track your progress and complete your certification.</p>
        </div>

        {loading ? (
          <div className="text-sm text-zinc-500 font-mono">Loading…</div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Progress */}
            <div className="lg:col-span-2 bg-white border-2 border-zinc-900 rounded-lg p-8" data-testid="dashboard-progress-card">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">Course progress</div>
                  <div className="font-display text-3xl font-bold mt-1" data-testid="progress-percent">{percent}%</div>
                </div>
                <BookOpen className="h-8 w-8 text-red-600" strokeWidth={1.5} />
              </div>
              <Progress value={percent} className="h-2" />
              <div className="mt-3 text-sm text-zinc-600">
                {progress?.completed_modules?.length || 0} of {progress?.total_modules || 10} modules complete
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <Button
                  onClick={() => navigate("/course")}
                  className="bg-zinc-900 hover:bg-zinc-800 text-white"
                  data-testid="dashboard-continue-btn"
                >
                  {percent === 0 ? "Start course" : percent === 100 ? "Review modules" : "Continue course"} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button
                  onClick={() => navigate("/exam")}
                  disabled={!canTakeExam}
                  variant={canTakeExam ? "default" : "outline"}
                  className={canTakeExam ? "bg-red-600 hover:bg-red-700 text-white" : ""}
                  data-testid="dashboard-take-exam-btn"
                >
                  <Trophy className="mr-2 h-4 w-4" />
                  {canTakeExam ? "Take final exam" : "Exam locked (finish modules)"}
                </Button>
              </div>
            </div>

            {/* Certificate */}
            <div
              className={`rounded-lg p-8 border-2 ${cert ? "bg-zinc-900 text-white border-zinc-900" : "bg-white border-zinc-200"}`}
              data-testid="dashboard-cert-card"
            >
              <Award className={`h-8 w-8 mb-6 ${cert ? "text-red-500" : "text-zinc-400"}`} strokeWidth={1.5} />
              <div className={`text-xs font-mono uppercase tracking-widest ${cert ? "text-zinc-400" : "text-zinc-500"}`}>
                Certificate
              </div>
              {cert ? (
                <>
                  <div className="font-display text-2xl font-bold mt-1">Earned</div>
                  <div className="text-sm text-zinc-400 mt-2">
                    Score: {cert.score}% · Valid until {String(cert.expires_at).slice(0, 10)}
                  </div>
                  {cert.paid ? (
                    <div className="mt-6 space-y-2">
                      <Button
                        onClick={openPreview}
                        variant="outline"
                        className="w-full border-zinc-600 bg-zinc-800 text-white hover:bg-zinc-700 hover:text-white"
                        data-testid="dashboard-preview-cert-btn"
                      >
                        <Eye className="mr-2 h-4 w-4" /> Preview certificate
                      </Button>
                      <Button
                        onClick={downloadCert}
                        className="w-full bg-red-600 hover:bg-red-700 text-white"
                        data-testid="dashboard-download-cert-btn"
                      >
                        <Download className="mr-2 h-4 w-4" /> Download PDF
                      </Button>
                    </div>
                  ) : (
                    <div className="mt-6">
                      <PayPalUnlockButton
                        certId={cert.certificate_id}
                        variant="dark"
                        priceLabel="$30"
                        testId="dashboard-unlock-cert-btn"
                        onSuccess={() => {
                          api.get("/progress").then((r) => setProgress(r.data)).then(downloadCert);
                        }}
                      />
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="font-display text-2xl font-bold mt-1">Not yet earned</div>
                  <div className="text-sm text-zinc-500 mt-2">
                    Complete all modules and pass the final exam with 80% or higher to earn your certificate.
                  </div>
                </>
              )}
            </div>

            {/* Legal disclaimer — mandatory on certificate page */}
            <div className="lg:col-span-3">
              <LegalDisclaimer />
            </div>

            {/* Practice Cases card */}
            <div className="lg:col-span-3 bg-white border border-zinc-200 rounded-lg p-8" data-testid="dashboard-practice-card">
              <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
                <div>
                  <div className="text-xs font-mono uppercase tracking-widest text-red-600 flex items-center gap-2">
                    <Sparkles className="h-3.5 w-3.5" /> AI Practice cases
                  </div>
                  <div className="font-display text-2xl font-bold mt-1">Practice sessions</div>
                  <div className="text-sm text-zinc-500 mt-1">
                    Profession-tailored case studies generated by Claude Sonnet 4.5. Not counted toward certification.
                  </div>
                </div>
                <Link
                  to="/course/practice-cases"
                  className="inline-flex items-center gap-2 text-sm font-medium bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md"
                  data-testid="dashboard-practice-start"
                >
                  <Briefcase className="h-4 w-4" /> Start practice
                </Link>
              </div>

              {practice && practice.total_sessions > 0 ? (
                <>
                  <div className="grid sm:grid-cols-3 gap-4 mb-6">
                    <div className="border-l-4 border-red-600 pl-4">
                      <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">Sessions</div>
                      <div className="font-display text-3xl font-black mt-1" data-testid="practice-total-sessions">{practice.total_sessions}</div>
                    </div>
                    <div className="border-l-4 border-emerald-600 pl-4">
                      <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">Best score</div>
                      <div className="font-display text-3xl font-black mt-1" data-testid="practice-best-score">{practice.best_percent}%</div>
                    </div>
                    <div className="border-l-4 border-zinc-900 pl-4">
                      <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">Average</div>
                      <div className="font-display text-3xl font-black mt-1" data-testid="practice-avg-score">{practice.avg_percent}%</div>
                    </div>
                  </div>

                  <div className="border-t border-zinc-200">
                    <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 pt-4 pb-2">Recent sessions</div>
                    <div className="divide-y divide-zinc-100" data-testid="practice-history-list">
                      {practice.scores.slice(0, 5).map((s) => (
                        <div key={s.session_id} className="py-3 flex items-center justify-between gap-3 text-sm">
                          <div className="flex items-center gap-3 min-w-0">
                            <Briefcase className="h-4 w-4 text-zinc-400 flex-shrink-0" />
                            <div className="min-w-0">
                              <div className="font-medium truncate">{s.profession}</div>
                              <div className="text-xs text-zinc-500 font-mono">{String(s.completed_at).slice(0, 10)}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className="font-mono text-xs text-zinc-500">{s.score}/{s.total}</span>
                            <span
                              className={`font-mono text-xs font-bold px-2 py-0.5 rounded ${
                                s.percent >= 80 ? "bg-emerald-50 text-emerald-700" :
                                s.percent >= 60 ? "bg-amber-50 text-amber-700" :
                                "bg-red-50 text-red-700"
                              }`}
                            >
                              {s.percent}%
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-sm text-zinc-500 border border-dashed border-zinc-300 rounded-md p-6 text-center" data-testid="practice-empty-state">
                  No practice sessions yet. Pick your profession and try 10 AI-generated case studies — you can practice as many times as you want.
                </div>
              )}
            </div>

            {/* My Purchases */}
            {purchases && purchases.count > 0 && (
              <div className="lg:col-span-3 bg-white border border-zinc-200 rounded-lg p-8 mt-2" data-testid="dashboard-purchases-card">
                <div className="flex items-baseline justify-between mb-4 flex-wrap gap-3">
                  <div>
                    <div className="text-xs font-mono uppercase tracking-widest text-red-600 flex items-center gap-2">
                      <Receipt className="h-3.5 w-3.5" /> My purchases
                    </div>
                    <div className="font-display text-2xl font-bold mt-1">
                      {purchases.count} transaction{purchases.count === 1 ? "" : "s"}
                    </div>
                    <div className="text-sm text-zinc-500 mt-1">Payment history for compliance and expense records.</div>
                  </div>
                </div>

                <div className="border border-zinc-200 rounded-lg overflow-hidden" data-testid="purchases-list">
                  <div className="grid grid-cols-12 bg-zinc-50 border-b border-zinc-200 py-2.5 px-4 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                    <div className="col-span-3">Date</div>
                    <div className="col-span-3">Method</div>
                    <div className="col-span-2">Amount</div>
                    <div className="col-span-2">Status</div>
                    <div className="col-span-2">Reference</div>
                  </div>
                  <div className="divide-y divide-zinc-100 text-sm">
                    {purchases.orders.map((o) => {
                      const isPaid = o.status === "paid";
                      const isCoupon = o.provider === "coupon";
                      const dateStr = String(o.paid_at || o.created_at || "").slice(0, 10);
                      const methodLabel = isCoupon ? "Coupon" : (o.mode === "live" ? "PayPal · Live" : "PayPal · Sandbox");
                      const MethodIcon = isCoupon ? Ticket : CreditCard;
                      return (
                        <div key={o.order_id} className="grid grid-cols-12 py-3 px-4 items-center" data-testid={`purchase-${o.order_id}`}>
                          <div className="col-span-3 font-mono text-xs text-zinc-700">{dateStr}</div>
                          <div className="col-span-3 flex items-center gap-2 text-xs">
                            <MethodIcon className="h-3.5 w-3.5 text-zinc-400" />
                            <span>{methodLabel}</span>
                            {o.test_mode && (
                              <span className="text-[9px] font-mono uppercase bg-amber-50 text-amber-800 px-1.5 py-0.5 rounded">TEST</span>
                            )}
                            {o.coupon_code && (
                              <span className="text-[9px] font-mono uppercase bg-emerald-50 text-emerald-800 px-1.5 py-0.5 rounded">{o.coupon_code}</span>
                            )}
                          </div>
                          <div className="col-span-2 font-mono font-bold">
                            {isCoupon ? "$0.00" : `$${o.amount}`}
                            <span className="text-[10px] text-zinc-400 ml-1">{o.currency || "USD"}</span>
                          </div>
                          <div className="col-span-2">
                            <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded ${
                              isPaid ? "bg-emerald-50 text-emerald-700" :
                              o.status === "created" ? "bg-amber-50 text-amber-700" :
                              "bg-red-50 text-red-700"
                            }`}>
                              {o.status}
                            </span>
                          </div>
                          <div className="col-span-2 font-mono text-[10px] text-zinc-500 truncate" title={o.capture_id || o.order_id}>
                            {o.capture_id || o.order_id}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="text-[11px] text-zinc-500 mt-4">
                  Need a receipt for reimbursement? Email <a href="mailto:admin@imcac.org" className="text-red-700 underline">admin@imcac.org</a> with your reference number.
                </div>
              </div>
            )}

            {/* Module list preview */}
            <div className="lg:col-span-3 bg-white border border-zinc-200 rounded-lg p-8 mt-2" data-testid="dashboard-modules-list">
              <div className="flex items-baseline justify-between mb-6">
                <div>
                  <div className="text-xs font-mono uppercase tracking-widest text-red-600">Modules</div>
                  <div className="font-display text-2xl font-bold">Your learning path</div>
                </div>
                <Link to="/course" className="text-sm text-red-600 hover:underline" data-testid="view-all-modules">
                  Open full course →
                </Link>
              </div>

              <div className="grid border-t border-l border-zinc-200">
                {modules.map((m, i) => (
                  <Link
                    key={m.slug}
                    to={`/course/${m.slug}`}
                    data-testid={`dashboard-module-${m.slug}`}
                    className="grid grid-cols-12 border-r border-b border-zinc-200 py-4 px-4 hover:bg-zinc-50 items-center transition-colors"
                  >
                    <div className="col-span-1 font-mono text-xs text-zinc-500">{String(i + 1).padStart(2, "0")}</div>
                    <div className="col-span-8 md:col-span-9">
                      <div className="font-medium">{m.title}</div>
                      <div className="text-xs text-zinc-500 mt-0.5">{m.subtitle}</div>
                    </div>
                    <div className="col-span-3 md:col-span-2 flex justify-end">
                      {m.completed ? (
                        <span className="inline-flex items-center gap-1 text-xs text-red-600 font-mono">
                          <CheckCircle2 className="h-3.5 w-3.5" /> DONE
                        </span>
                      ) : (
                        <span className="text-xs text-zinc-400 font-mono">{m.duration_min} MIN</span>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Certificate preview modal */}
      <Dialog open={previewOpen} onOpenChange={(o) => (o ? setPreviewOpen(true) : closePreview())}>
        <DialogContent
          className="max-w-5xl w-[95vw] h-[90vh] p-0 gap-0 flex flex-col [&>button:last-child]:hidden"
          data-testid="cert-preview-modal"
        >
          <DialogHeader className="px-6 py-4 border-b border-zinc-200 flex-row items-center justify-between space-y-0">
            <DialogTitle className="font-display text-lg">Certificate preview</DialogTitle>
            <DialogDescription className="sr-only">
              Inline preview of your IMCAC/DOOSP certificate PDF. Use the Download button to save a copy.
            </DialogDescription>
            <div className="flex items-center gap-2">
              <Button
                onClick={downloadCert}
                size="sm"
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="cert-preview-download-btn"
              >
                <Download className="mr-2 h-4 w-4" /> Download PDF
              </Button>
              <Button
                onClick={closePreview}
                size="sm"
                variant="ghost"
                className="h-9 w-9 p-0"
                aria-label="Close preview"
                data-testid="cert-preview-close-btn"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </DialogHeader>
          <div className="flex-1 min-h-0 bg-zinc-100">
            {previewLoading && (
              <div className="flex items-center justify-center h-full text-sm font-mono text-zinc-500" data-testid="cert-preview-loading">
                Loading certificate…
              </div>
            )}
            {!previewLoading && previewUrl && (
              <iframe
                src={previewUrl}
                title="Certificate preview"
                className="w-full h-full border-0"
                data-testid="cert-preview-iframe"
              />
            )}
            {!previewLoading && !previewUrl && (
              <div className="flex items-center justify-center h-full text-sm font-mono text-red-600" data-testid="cert-preview-error">
                Could not load certificate. Please try downloading directly.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
